import type { VoiceNoteFxParams } from "../types";

/** Per-voice runtime state for tracker note FX. */
export type VoiceNoteFxState = {
  vibratoSpeedHz: number;
  vibratoDepthSemi: number;
  vibratoPhase: number;
  slideSemiPerSec: number;
  portaSemiPerSec: number;
  cutoffOct: number;
  arpSemi1: number;
  arpSemi2: number;
  arpHz: number;
  arpPhase: number;
  /** Voice pan −1..1; null = inherit slot mix pan. */
  pan: number | null;
};

export function createNoteFxState(): VoiceNoteFxState {
  return {
    vibratoSpeedHz: 0,
    vibratoDepthSemi: 0,
    vibratoPhase: 0,
    slideSemiPerSec: 0,
    portaSemiPerSec: 0,
    cutoffOct: 0,
    arpSemi1: 0,
    arpSemi2: 0,
    arpHz: 0,
    arpPhase: 0,
    pan: null,
  };
}

/** Fresh note-on: replace active FX from decoded params (absent fields clear). */
export function resetNoteFxFromParams(
  state: VoiceNoteFxState,
  params: VoiceNoteFxParams | undefined,
): void {
  state.vibratoSpeedHz = params?.vibratoSpeedHz ?? 0;
  state.vibratoDepthSemi = params?.vibratoDepthSemi ?? 0;
  state.vibratoPhase = 0;
  state.slideSemiPerSec = params?.slideSemiPerSec ?? 0;
  state.portaSemiPerSec = params?.portaSemiPerSec ?? 0;
  state.cutoffOct = params?.cutoffOct ?? 0;
  state.arpSemi1 = params?.arpSemi1 ?? 0;
  state.arpSemi2 = params?.arpSemi2 ?? 0;
  state.arpHz = params?.arpHz ?? 0;
  state.arpPhase = 0;
  state.pan = params?.pan !== undefined ? clampPan(params.pan) : null;
}

/** FX-only row: merge / override without clearing unspecified fields. */
export function patchNoteFxFromParams(
  state: VoiceNoteFxState,
  params: VoiceNoteFxParams,
): void {
  if (params.vibratoSpeedHz !== undefined) {
    state.vibratoSpeedHz = params.vibratoSpeedHz;
  }
  if (params.vibratoDepthSemi !== undefined) {
    state.vibratoDepthSemi = params.vibratoDepthSemi;
  }
  if (params.slideSemiPerSec !== undefined) {
    state.slideSemiPerSec = params.slideSemiPerSec;
  }
  if (params.portaSemiPerSec !== undefined) {
    state.portaSemiPerSec = params.portaSemiPerSec;
  }
  if (params.cutoffOct !== undefined) {
    state.cutoffOct = params.cutoffOct;
  }
  if (
    params.arpSemi1 !== undefined
    || params.arpSemi2 !== undefined
    || params.arpHz !== undefined
  ) {
    if (params.arpSemi1 !== undefined) {
      state.arpSemi1 = params.arpSemi1;
    }
    if (params.arpSemi2 !== undefined) {
      state.arpSemi2 = params.arpSemi2;
    }
    if (params.arpHz !== undefined) {
      state.arpHz = params.arpHz;
    }
    state.arpPhase = 0;
  }
  if (params.pan !== undefined) {
    state.pan = clampPan(params.pan);
  }
}

function clampPan(pan: number): number {
  return Math.min(1, Math.max(-1, pan));
}

function arpeggioSemi(state: VoiceNoteFxState, dt: number): number {
  if (state.arpHz <= 0) {
    return 0;
  }
  const tones: number[] = [0];
  if (state.arpSemi1 > 0) {
    tones.push(state.arpSemi1);
  }
  if (state.arpSemi2 > 0) {
    tones.push(state.arpSemi2);
  }
  if (tones.length <= 1) {
    return 0;
  }
  state.arpPhase += state.arpHz * dt;
  const idx = Math.floor(state.arpPhase) % tones.length;
  return tones[idx]!;
}

/**
 * Advance pitch targets for slide / porta. Mutates freqHz & targetFreqHz.
 * Returns vibrato + arpeggio depth to add to pitchMod (semitones).
 */
export function advanceNoteFxPitch(
  state: VoiceNoteFxState,
  voice: { freqHz: number; targetFreqHz: number },
  dt: number,
  presetGlideMs: number | undefined,
): number {
  if (state.slideSemiPerSec !== 0) {
    const ratio = 2 ** ((state.slideSemiPerSec * dt) / 12);
    voice.targetFreqHz *= ratio;
    // Keep audible pitch with the slide unless portamento is steering.
    if (state.portaSemiPerSec <= 0) {
      voice.freqHz *= ratio;
    }
  }

  if (state.portaSemiPerSec > 0) {
    const cur = Math.log2(Math.max(voice.freqHz, 1e-3));
    const tgt = Math.log2(Math.max(voice.targetFreqHz, 1e-3));
    const maxStep = (state.portaSemiPerSec / 12) * dt;
    const delta = Math.min(maxStep, Math.max(-maxStep, tgt - cur));
    voice.freqHz = 2 ** (cur + delta);
  } else if (presetGlideMs && presetGlideMs > 0) {
    const glide = Math.exp(-dt * (1000 / presetGlideMs));
    voice.freqHz = voice.targetFreqHz + (voice.freqHz - voice.targetFreqHz) * glide;
  } else {
    voice.freqHz = voice.targetFreqHz;
  }

  let pitchSemi = arpeggioSemi(state, dt);
  if (state.vibratoSpeedHz > 0 && state.vibratoDepthSemi > 0) {
    state.vibratoPhase += state.vibratoSpeedHz * dt;
    pitchSemi += state.vibratoDepthSemi * Math.sin(2 * Math.PI * (state.vibratoPhase % 1));
  }
  return pitchSemi;
}
